import { BenefitsBenefitAvailabilityFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

export type BenefitStatus = 'active' | 'available' | 'pending' | 'not_active'

type SpanishBenefitId =
  | 'meals_es'
  | 'transport_es'
  | 'childcare_es'
  | 'health_insurance_es'
  | 'trainings_es'
  | 'wellness_es'
type PortugueseBenefitId = 'meals_pt' | 'health_insurance_pt' | 'wellness_pt'
type GermanBenefitId = '50_in_kind_de' | '60_special_events_de' | 'wellness_de'
export type SupportedBenefitId = SpanishBenefitId | PortugueseBenefitId | GermanBenefitId

export interface BenefitListing {
  id: SupportedBenefitId
  title: string
  description: string
  reviewDescription?: string
  benefitStatus?: BenefitStatus
  emoji: string
  learnMoreUrl?: string
}

const spainBenefits: (
  featureAvailabilities?: BenefitsBenefitAvailabilityFragment[]
) => BenefitListing[] = (featureAvailabilities) => {
  const trainingsEnabled = featureAvailabilities?.find(
    (feature) => feature.id === 'benefits_trainings'
  )?.available

  return [
    {
      id: 'meals_es',
      title: i18n.t('benefits.homepage.static_benefits.es.meals.title'),
      description: i18n.t('benefits.homepage.static_benefits.es.meals.description'),
      reviewDescription: i18n.t(
        'benefits.tabs.employees.activation_results.modal.review_benefits.benefits_list.meals.description'
      ),
      emoji: '🍔',
      benefitStatus: 'active',
      learnMoreUrl:
        'https://help.factorialhr.com/beneficios/about-flexible-compensation-for-restaurant-tickets',
    },
    {
      id: 'transport_es',
      title: i18n.t('benefits.homepage.static_benefits.es.transport.title'),
      description: i18n.t('benefits.homepage.static_benefits.transport.description'),
      reviewDescription: i18n.t(
        'benefits.tabs.employees.activation_results.modal.review_benefits.benefits_list.transport.description'
      ),
      emoji: '🚊',
      benefitStatus: 'active',
      learnMoreUrl:
        'https://help.factorialhr.com/beneficios/about-flexible-compensation-for-restaurant-tickets',
    },
    {
      id: 'childcare_es',
      title: i18n.t('benefits.homepage.static_benefits.es.nursery.title'),
      description: i18n.t('benefits.homepage.static_benefits.es.nursery.description'),
      reviewDescription: i18n.t(
        'benefits.tabs.employees.activation_results.modal.review_benefits.benefits_list.childcare.description'
      ),
      emoji: '👶',
      benefitStatus: 'active',
      learnMoreUrl:
        'https://help.factorialhr.com/beneficios/about-flexible-remuneration-nursery-tickets',
      primaryAction: i18n.t('benefits.homepage.benefit.request'),
    },
    {
      id: 'health_insurance_es',
      title: i18n.t('benefits.homepage.static_benefits.es.health_insurance.title'),
      emoji: '🏥',
      description: i18n.t('benefits.homepage.static_benefits.es.health_insurance.description'),
      benefitStatus: 'pending',
    },
    {
      id: 'trainings_es',
      title: i18n.t('benefits.homepage.static_benefits.es.trainings.title'),
      emoji: '📚',
      description: i18n.t('benefits.homepage.static_benefits.es.trainings.description'),
      benefitStatus: featureAvailabilities
        ? trainingsEnabled
          ? 'active'
          : 'not_active'
        : undefined,
    },
    {
      id: 'wellness_es',
      title: i18n.t('benefits.homepage.static_benefits.es.wellness.title'),
      emoji: '👟',
      description: i18n.t('benefits.homepage.static_benefits.es.wellness.description'),
      benefitStatus: 'pending',
    },
  ]
}

const portugalBenefits: (
  featureAvailabilities?: BenefitsBenefitAvailabilityFragment[]
) => BenefitListing[] = () => {
  return [
    {
      id: 'meals_pt',
      title: i18n.t('benefits.homepage.static_benefits.pt.meals.title'),
      emoji: '🍔',
      description: i18n.t('benefits.homepage.static_benefits.pt.meals.description'),
      benefitStatus: 'active',
    },
    {
      id: 'health_insurance_pt',
      title: i18n.t('benefits.homepage.static_benefits.pt.health_insurance.title'),
      emoji: '🏥',
      description: i18n.t('benefits.homepage.static_benefits.pt.health_insurance.description'),
      benefitStatus: 'pending',
    },
    {
      id: 'wellness_pt',
      title: i18n.t('benefits.homepage.static_benefits.pt.wellness.title'),
      emoji: '👟',
      description: i18n.t('benefits.homepage.static_benefits.pt.wellness.description'),
      benefitStatus: 'pending',
    },
  ]
}

const germanyBenefits: (
  featureAvailabilities?: BenefitsBenefitAvailabilityFragment[]
) => BenefitListing[] = () => {
  return [
    {
      id: '50_in_kind_de',
      title: i18n.t('benefits.homepage.static_benefits.de.50_in_kind.title'),
      emoji: '💰',
      description: i18n.t('benefits.homepage.static_benefits.de.50_in_kind.description'),
      benefitStatus: 'active',
    },
    {
      id: '60_special_events_de',
      title: i18n.t('benefits.homepage.static_benefits.de.60_special_events.title'),
      emoji: '💸',
      description: i18n.t('benefits.homepage.static_benefits.de.60_special_events.description'),
      benefitStatus: 'pending',
    },
    {
      id: 'wellness_de',
      title: i18n.t('benefits.homepage.static_benefits.de.wellness.title'),
      emoji: '👟',
      description: i18n.t('benefits.homepage.static_benefits.de.wellness.description'),
      benefitStatus: 'pending',
    },
  ]
}

export const getBenefitsList = (
  country: string,
  featureAvailabilities?: BenefitsBenefitAvailabilityFragment[]
): BenefitListing[] => {
  switch (country) {
    case 'pt':
      return portugalBenefits(featureAvailabilities)
    case 'de':
      return germanyBenefits(featureAvailabilities)
    default:
      return spainBenefits(featureAvailabilities)
  }
}
